import React, { useRef, useState } from "react";
import { UserDetails, useUser } from "../../components/UserContext";
import { putMyProfile } from "../../../../shared/services/services";
import { notifyError, notifySuccess, notifyWarning } from "../../../../assets/toastService";
import Image from "src/shared/Image";
import { useImageUpload } from "src/shared/modules/profile/hooks/useImageUpload";
import useUsers from "../../hooks/useUsers";

type Props = {
  user: UserDetails;
  prodileId?: any;
};

// Converts any date string/object to "15 Dec 2025" display format
function formatDateDisplay(raw: string): string {
  const date = new Date(raw);
  if (isNaN(date.getTime())) return raw;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }); // → "15 Dec 2025"
}

// Safely extract a string from a field that may be a primitive or an object
function extractString(value: any, isDate = false): string {
  if (!value) return "";
  if (typeof value === "string") {
    // Handles "Mon, 15 Dec 2025 00:00:00 GMT" and ISO strings
    return isDate ? formatDateDisplay(value) : value;
  }
  if (typeof value === "number") return String(value);
  // { email, isPrimary } shape
  if (typeof value === "object" && "email" in value) return value.email ?? "";
  // { phone, isPrimary } or { number, ... } shapes
  if (typeof value === "object" && "phone" in value) return value.phone ?? "";
  if (typeof value === "object" && "number" in value) return value.number ?? "";
  // MongoDate: { $date: "..." }
  if (typeof value === "object" && "$date" in value)
    return isDate ? formatDateDisplay(value.$date) : (value.$date?.split("T")[0] ?? "");
  // Array of objects — take the primary one or the first
  if (Array.isArray(value)) {
    const primary = value.find((v: any) => v?.isPrimary) ?? value[0];
    return extractString(primary, isDate);
  }
  return "";
}

function MyProfile({ user, prodileId }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const { reload } = useUsers()
  const [formData, setFormData] = useState({
    name: extractString(user.firstName),
    phone_number: extractString(user.phones),
    email_id: extractString(user.emails?.[0]?.email),
    date_of_birth: extractString(user.dob, true),
  });
  const { uploadImage } = useImageUpload();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdate = () => {
    setIsEditing(false);

    putMyProfile(formData, user?.orgId)
      .then(() => {
        notifySuccess("Profile updated successfully");
        reload()
      })
      .catch((error) => {
        console.error(error);
        notifyError(error);
      });
  };

  // Original values for dirty-checking — also extracted as strings
  const originalValues = {
    name: extractString(user.firstName),
    phone_number: extractString(user.phones),
    email_id: extractString(user.emails?.[0]?.email),
    date_of_birth: extractString(user.dob, true),
  };

  const renderField = (
    label: string,
    field: keyof typeof formData,
    readonly: boolean = false
  ) => (
    <div className='w-full flex flex-col md:flex-row gap-2 text-base md:text-lg'>
      <p className='md:w-1/3 font-medium'>{label}</p>

      <div className='md:w-2/3'>
        {isEditing && !readonly ? (
          <input
            type='text'
            value={formData[field]}
            maxLength={field === "phone_number" ? 12 : 40}
            onChange={(e) => handleChange(field, e.target.value)}
            className={`w-full border px-3 py-2 rounded ${formData[field] !== originalValues[field]
              ? "border-blue-500"
              : "border-gray-300"
              }`}
          />
        ) : (
          // formData[field] is always a string now — safe to render
          <p className='break-words'>: {formData[field]}</p>
        )}
      </div>
    </div>
  );

  const isFormUnchanged = () =>
    formData.name === originalValues.name &&
    formData.phone_number === originalValues.phone_number &&
    formData.email_id === originalValues.email_id &&
    formData.date_of_birth === originalValues.date_of_birth;

  const handleImageBoxClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    const img = document.createElement("img");

    reader.onload = () => {
      if (typeof reader.result === "string") {
        img.src = reader.result;
      }
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const maxWidth = 200;
      const scaleFactor = maxWidth / img.width;

      canvas.width = maxWidth;
      canvas.height = img.height * scaleFactor;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob && blob.size) {
          const compressedFile = new File([blob], "image.png", { type: "image/png" });
          uploadImage(
            compressedFile,
            `/pos/v2/image/${prodileId}`,
            "put",
            `${user?.userName}.png`,
            `Profilelogo${user?.userName}`
          );
        } else {
          notifyWarning("Image png format required");
        }
      }, "image/png");
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className='w-full h-full overflow-auto p-4 flex justify-center'>
      {/* Card */}
      <div className='w-full max-w-2xl border border-gray-200 shadow-xl rounded-2xl p-6 bg-white h-fit'>
        {/* Header */}
        <div className='flex flex-col md:flex-row items-center md:items-start gap-4 border-b pb-4'>
          {/* Profile Image */}
          <div onClick={handleImageBoxClick} className='w-28 h-28 rounded-full overflow-hidden border cursor-pointer'>
            <Image imagePath={user?.profileImageUrl || ""} className='w-full h-full object-cover' profile={true} />
            <input type='file' accept='image/*' ref={fileInputRef} onChange={handleImageChange} className='hidden' />
          </div>

          {/* Title + Buttons */}
          <div className='flex items-center justify-between flex-1 gap-4'>
            <h1 className='text-2xl font-semibold mt-9'>My Profile</h1>

            <div className='flex flex-wrap gap-2'>
              {/* <button
                onClick={() => setIsEditing(!isEditing)}
                className='bg-blue-600 px-4 py-2 text-white rounded-full mb-2'
              >
                {isEditing ? "Cancel" : "Edit Profile"}
              </button> */}
            </div>
          </div>
        </div>

        {/* Details */}
        <div className='mt-6 space-y-4'>
          <h2 className='text-xl font-semibold'>Profile Details</h2>

          {renderField("Name", "name", true)}
          {renderField("Phone Number", "phone_number", true)}
          {renderField("Email", "email_id", true)}
          {renderField("Date Of Birth", "date_of_birth", true)}
        </div>

        {/* Update Button */}
        {isEditing && (
          <div className='mt-6 flex justify-center'>
            <button
              onClick={handleUpdate}
              className={`px-8 py-2 rounded-full text-white ${isFormUnchanged()
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
                }`}
              disabled={isFormUnchanged()}
            >
              Update
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyProfile;